import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import type { Board, UUID } from "@/lib/types";

interface Vars {
  boardId: UUID;
  workspaceId: UUID;
}

// F2: archive a board — it leaves the main lists (My Boards / pinned) and shows
// only under the workspace "Archiviert" filter, which keeps it in the
// workspace-scoped boards query (archived_at flipped on).
export function useArchiveBoard() {
  const qc = useQueryClient();
  return useMutation<void, Error, Vars>({
    mutationFn: ({ boardId }) => api.archiveBoard(boardId),
    onSuccess: (_void, { boardId, workspaceId }) => {
      qc.setQueryData<Board[]>(queryKeys.myBoards, (prev) =>
        prev ? prev.filter((b) => b.id !== boardId) : prev,
      );
      qc.setQueryData<Board[]>(queryKeys.pinnedBoards, (prev) =>
        prev ? prev.filter((b) => b.id !== boardId) : prev,
      );
      // The workspace grid keeps archived boards (for the Archiviert pill) — flip
      // the flag in place so it moves into that bucket immediately, then refetch.
      qc.setQueryData<Board[]>(queryKeys.boards(workspaceId), (prev) =>
        prev
          ? prev.map((b) =>
              b.id === boardId ? { ...b, archived_at: new Date().toISOString() } : b,
            )
          : prev,
      );
      void qc.invalidateQueries({ queryKey: queryKeys.boards(workspaceId) });
      void qc.invalidateQueries({ queryKey: queryKeys.myBoards });
      void qc.invalidateQueries({ queryKey: queryKeys.pinnedBoards });
    },
  });
}

export function useUnarchiveBoard() {
  const qc = useQueryClient();
  return useMutation<void, Error, Vars>({
    mutationFn: ({ boardId }) => api.unarchiveBoard(boardId),
    onSuccess: (_void, { boardId, workspaceId }) => {
      qc.setQueryData<Board[]>(queryKeys.boards(workspaceId), (prev) =>
        prev ? prev.map((b) => (b.id === boardId ? { ...b, archived_at: null } : b)) : prev,
      );
      void qc.invalidateQueries({ queryKey: queryKeys.boards(workspaceId) });
      void qc.invalidateQueries({ queryKey: queryKeys.myBoards });
      void qc.invalidateQueries({ queryKey: queryKeys.pinnedBoards });
    },
  });
}
